/**
 * NotesFilters Component
 *
 * Inline filter controls for notes list including date range and provider selection
 */

import React, { useCallback, useMemo } from 'react';
import { DateRangeSelector } from '@components/DateRangeSelector/DateRangeSelector';
import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useProvidersList } from '../../hooks/useProvidersList';

export interface NotesFiltersState {
  /** Start date (ISO format) */
  dateFrom?: string;
  /** End date (ISO format) */
  dateTo?: string;
  /** Period in days (alternative to date range) */
  days?: number;
  /** Selected provider IDs */
  userIds?: string[];
  /** Completion status filter */
  completionStatus?: 'all' | 'completed' | 'draft';
}

interface NotesFiltersProps {
  /** Current filter values */
  filters: NotesFiltersState;
  /** Callback when filters change */
  onFiltersChange: (filters: NotesFiltersState) => void;
  /** Whether data is loading */
  isLoading?: boolean;
}

export const NotesFilters: React.FC<NotesFiltersProps> = ({
  filters,
  onFiltersChange,
  isLoading = false,
}) => {
  const { jotauditDashboard } = useEventTracking();
  
  // Fetch providers for the multi-select
  const { providers: allProviders, isLoading: isLoadingProviders } = useProvidersList();

  const handleDateRangeChange = useCallback(
    (range: { start: Date | null; end: Date | null }) => {
      const previousValue = filters.dateFrom
        ? `${filters.dateFrom}_${filters.dateTo}`
        : filters.days?.toString() || 'none';
      const newValue = range.start
        ? `${range.start.toISOString().split('T')[0]}_${range.end?.toISOString().split('T')[0]}`
        : 'none';

      // Track filter change
      jotauditDashboard.notesFilterChanged({
        filter_type: 'date_range',
        previous_value: previousValue,
        new_value: newValue,
      });

      onFiltersChange({
        ...filters,
        dateFrom: range.start ? range.start.toISOString().split('T')[0] : undefined,
        dateTo: range.end ? range.end.toISOString().split('T')[0] : undefined,
        days: undefined, // Clear days when using date range
      });
    },
    [filters, onFiltersChange, jotauditDashboard],
  );

  const handleDaysChange = useCallback(
    (days: number | undefined) => {
      const previousValue = filters.days?.toString() || 'none';
      const newValue = days?.toString() || 'none';

      // Track filter change
      jotauditDashboard.notesFilterChanged({
        filter_type: 'days',
        previous_value: previousValue,
        new_value: newValue,
      });

      onFiltersChange({
        ...filters,
        days,
        dateFrom: undefined, // Clear date range when using days
        dateTo: undefined,
      });
    },
    [filters, onFiltersChange, jotauditDashboard],
  );

  const handleProvidersChange = useCallback(
    (_event: React.SyntheticEvent, newValue: { id: string; label: string }[]) => {
      const previousCount = filters.userIds?.length || 0;
      const newCount = newValue.length;

      // Track filter change
      jotauditDashboard.notesFilterChanged({
        filter_type: 'clinicians',
        previous_value: previousCount.toString(),
        new_value: newCount.toString(),
      });

      onFiltersChange({
        ...filters,
        userIds: newValue.length > 0 ? newValue.map(p => p.id) : undefined,
      });
    },
    [filters, onFiltersChange, jotauditDashboard],
  );

  const dateRangeValue = useMemo(() => {
    if (filters.dateFrom && filters.dateTo) {
      return {
        start: new Date(filters.dateFrom),
        end: new Date(filters.dateTo),
      };
    }
    return { start: null, end: null };
  }, [filters.dateFrom, filters.dateTo]);

  const providerOptions = useMemo(
    () =>
      allProviders.map(p => ({
        id: p.user_id,
        label: p.provider_name || p.user_id,
      })),
    [allProviders],
  );

  const selectedProviders = useMemo(
    () => providerOptions.filter(p => filters.userIds?.includes(p.id)),
    [providerOptions, filters.userIds],
  );

  const hasActiveFilters = useMemo(() => {
    return !!(
      filters.dateFrom ||
      filters.dateTo ||
      filters.days ||
      filters.userIds?.length
    );
  }, [filters]);

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        mb: 3,
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        {/* Date Range Selector */}
        <Box sx={{ minWidth: 250 }}>
          <DateRangeSelector
            value={dateRangeValue}
            onChange={handleDateRangeChange}
            label="Date Range"
            size="small"
            disabled={isLoading}
            fullWidth
          />
        </Box>

        {/* Days Preset Chips */}
        {!filters.dateFrom && !filters.dateTo && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Quick:
            </Typography>
            {[7, 30, 90].map(days => (
              <Chip
                key={days}
                label={`${days}d`}
                onClick={() => handleDaysChange(filters.days === days ? undefined : days)}
                color={filters.days === days ? 'primary' : 'default'}
                size="small"
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        )}

        {/* Provider Multi-Select */}
        <Box sx={{ minWidth: 250, flex: 1, maxWidth: 400 }}>
          <Autocomplete
            multiple
            size="small"
            options={providerOptions}
            value={selectedProviders}
            onChange={handleProvidersChange}
            loading={isLoadingProviders}
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={params => (
              <TextField {...params} label="Filter by Provider" placeholder="All providers" />
            )}
            renderValue={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    label={option.label}
                    size="small"
                    {...tagProps}
                  />
                );
              })
            }
            disabled={isLoading}
          />
        </Box>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <Chip
            label="Clear"
            onClick={() =>
              onFiltersChange({
                dateFrom: undefined,
                dateTo: undefined,
                days: undefined,
                userIds: undefined,
              })
            }
            size="small"
            sx={{ cursor: 'pointer', ml: 'auto' }}
          />
        )}
      </Box>
    </Box>
  );
};

